import {
  ButtonCircle,
  IconNext,
} from '@momentum-ui/react-collaboration';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line import/no-relative-packages
import { DispositionTypes } from '../../../adapter-interfaces/src/ICallHistoryAdapter';
import { ICallHistoryItemProps } from '../components/CallHistory';
import { CallHistoryAvatar } from '../components/CallHistory/CallHistoryAvatar';
import {
  DesktopOutgoingCallIcon,
  MobileAudioCallIcon,
  MobileBackIcon,
  MobileVideoCallIcon,
} from '../components/MobileActionIcons/MobileActionIcons';
import {
  formatDate,
  formatDateDDMMYYYY,
  formatDurationFromSeconds,
  formatTime,
  formatTimeToSupport24Hours,
} from '../utils/dateUtils';
import { MAKE_A_CALL_LABELS } from '../utils/MetricUtils';
import './MobileCallHistory.styles.scss';

type MobileCallHistoryFilter = 'all' | 'missed';

export interface IMobileCallHistoryProps {
  items: ICallHistoryItemProps[];
  isLocaleGerman?: boolean;
  labelUnknown?: string;
  makeCall: (address: string, isVideo?: boolean, label?: string) => void;
  onItemPress: (item: ICallHistoryItemProps) => void;
  onDeleteCall?: (id: string, endTime: string) => void;
  onFilteredItemsChange?: (itemIds: string[]) => void;
  filter?: MobileCallHistoryFilter;
  hideFilters?: boolean;
  onRestoreParentFocus?: () => void;
  onDetailOpenChange?: (isOpen: boolean) => void;
}

export const MobileCallHistory = ({
  items,
  isLocaleGerman = false,
  labelUnknown = undefined,
  makeCall,
  onItemPress,
  onDeleteCall,
  onFilteredItemsChange,
  filter: filterProp,
  hideFilters = false,
  onRestoreParentFocus,
  onDetailOpenChange,
}: IMobileCallHistoryProps): JSX.Element => {
  const { t } = useTranslation('WebexCallHistory');
  const [uncontrolledFilter, setUncontrolledFilter] =
    useState<MobileCallHistoryFilter>(filterProp ?? 'all');
  const filter = filterProp ?? uncontrolledFilter;
  const setFilter = (nextFilter: MobileCallHistoryFilter) => {
    if (filterProp === undefined) {
      setUncontrolledFilter(nextFilter);
    }
  };
  const [selectedId, setSelectedId] = useState<string>();
  const restoreFocusId = useRef<string>();
  const lastRestoredFocusId = useRef<string>();
  const backButtonRef = useRef<HTMLButtonElement>(null);
  const allFilterRef = useRef<HTMLButtonElement>(null);
  const missedFilterRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLElement>(null);
  const rowRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const wasInDetail = useRef(false);

  const filteredItems = useMemo(
    () =>
      filter === 'missed'
        ? items.filter(
            (item) =>
              item.disposition?.toUpperCase() === DispositionTypes.MISSED
          )
        : items,
    [filter, items]
  );
  const filteredItemIds = useMemo(
    () => filteredItems.map(({ id }) => id),
    [filteredItems]
  );

  useEffect(() => {
    onFilteredItemsChange?.(filteredItemIds);
  }, [filteredItemIds, onFilteredItemsChange]);

  const selectedItem = items.find((item) => item.id === selectedId);
  useEffect(() => {
    onDetailOpenChange?.(!!selectedItem);
  }, [onDetailOpenChange, selectedItem]);
  const focusActiveFilter = useCallback(() => {
    const filterRef = filter === 'missed' ? missedFilterRef : allFilterRef;
    filterRef.current?.focus();
  }, [filter]);
  const focusFallbackTarget = useCallback(() => {
    if (hideFilters) {
      onRestoreParentFocus?.();
      if (document.activeElement !== document.body) {
        return;
      }
      listRef.current?.focus();
      return;
    }

    focusActiveFilter();
  }, [focusActiveFilter, hideFilters, onRestoreParentFocus]);

  useEffect(() => {
    if (selectedItem) {
      if (!wasInDetail.current) {
        backButtonRef.current?.focus();
      }
      wasInDetail.current = true;
      return;
    }

    if (selectedId) {
      setSelectedId(undefined);
      return;
    }

    wasInDetail.current = false;

    if (restoreFocusId.current) {
      const row = rowRefs.current.get(restoreFocusId.current);
      if (row) {
        row.focus();
        lastRestoredFocusId.current = restoreFocusId.current;
      } else {
        focusFallbackTarget();
      }
      restoreFocusId.current = undefined;
      return;
    }

    if (
      lastRestoredFocusId.current &&
      !filteredItems.some(({ id }) => id === lastRestoredFocusId.current)
    ) {
      focusFallbackTarget();
      lastRestoredFocusId.current = undefined;
    }
  }, [
    filteredItems,
    focusFallbackTarget,
    selectedId,
    selectedItem,
  ]);

  if (selectedItem) {
    const callAddress =
      selectedItem.callbackAddress || selectedItem.phoneNumber;
    const displayName =
      selectedItem.name ||
      selectedItem.phoneNumber ||
      selectedItem.callbackAddress ||
      labelUnknown ||
      'Unknown';
    const isMissed =
      selectedItem.disposition?.toUpperCase() === DispositionTypes.MISSED;
    let directionLabel = t('incomingCallText');
    if (selectedItem.direction?.toUpperCase() === 'OUTGOING') {
      directionLabel = t('outGoingCallText');
    }

    return (
      <section
        className="mobile-call-history mobile-call-history--detail"
        data-testid="mobile-call-history-detail"
      >
        <header className="mobile-call-history__detail-header">
          <button
            type="button"
            ref={backButtonRef}
            className="mobile-call-history__back"
            aria-label={t('mobile.backToRecentCalls', {
              defaultValue: 'Back to recent calls',
            })}
            onClick={() => setSelectedId(undefined)}
          >
            <MobileBackIcon size={24} />
          </button>
          <h2>
            {t('mobile.callDetails', {
              defaultValue: 'Call details',
            })}
          </h2>
        </header>

        <div className="mobile-call-history__identity">
          <CallHistoryAvatar
            remotePartyId={selectedItem.remotePartyId}
            fallbackId={selectedItem.id}
            displayName={displayName}
            title={selectedItem.name || displayName}
            size={72}
            requestSize={80}
            presenceType={selectedItem.presenceType}
            presenceLabel={selectedItem.presenceLabel}
          />
          <h3>{displayName}</h3>
          <p>{selectedItem.phoneNumber || selectedItem.callbackAddress}</p>
        </div>

        <div
          className="mobile-call-history__detail-actions"
          aria-label={t('mobile.callActions', {
            defaultValue: 'Call actions',
          })}
        >
          <div>
            <ButtonCircle
              size={64}
              color="join"
              onPress={() =>
                callAddress &&
                makeCall(
                  callAddress,
                  false,
                  MAKE_A_CALL_LABELS.RECENT_CALLS
                )
              }
              disabled={!callAddress}
              aria-label={t('mobile.audio', { defaultValue: 'Audio' })}
            >
              <MobileAudioCallIcon size={28} />
            </ButtonCircle>
            <span>{t('mobile.audio', { defaultValue: 'Audio' })}</span>
          </div>
          <div>
            <ButtonCircle
              size={64}
              color="join"
              onPress={() =>
                callAddress &&
                makeCall(
                  callAddress,
                  true,
                  MAKE_A_CALL_LABELS.RECENT_CALLS
                )
              }
              disabled={!callAddress}
              aria-label={t('mobile.video', { defaultValue: 'Video' })}
            >
              <MobileVideoCallIcon size={28} />
            </ButtonCircle>
            <span>{t('mobile.video', { defaultValue: 'Video' })}</span>
          </div>
          <div>
            <ButtonCircle
              size={64}
              outline
              ghost
              onPress={() => {
                onDeleteCall?.(selectedItem.id, selectedItem.endTime);
                setSelectedId(undefined);
              }}
              aria-label={t('deleteThisCall')}
            >
              <IconNext name="delete" scale={28} />
            </ButtonCircle>
            <span>{t('deleteThisCall')}</span>
          </div>
        </div>

        <div className="mobile-call-history__detail-date">
          <span>
            {isLocaleGerman
              ? formatDateDDMMYYYY(selectedItem.startTime)
              : formatDate(selectedItem.startTime)}
          </span>
        </div>
        <dl className="mobile-call-history__detail-record">
          <div>
            <dt>
              {isLocaleGerman
                ? formatTimeToSupport24Hours(selectedItem.startTime)
                : formatTime(selectedItem.startTime)}
            </dt>
            <dd>{directionLabel}</dd>
            <dd className={isMissed ? 'mobile-call-history__missed' : undefined}>
              {isMissed
                ? t('missedCallText')
                : formatDurationFromSeconds(selectedItem.durationSeconds || 0)}
            </dd>
          </div>
        </dl>
      </section>
    );
  }

  return (
    <section
      ref={listRef}
      className="mobile-call-history"
      data-testid="mobile-call-history"
      role={hideFilters ? 'region' : undefined}
      aria-label={
        hideFilters
          ? t(
              filter === 'missed'
                ? 'mobile.missedCallsList'
                : 'mobile.recentCallsList',
              {
                defaultValue:
                  filter === 'missed' ? 'Missed calls list' : 'Recent calls list',
              }
            )
          : undefined
      }
      tabIndex={hideFilters ? -1 : undefined}
    >
      {!hideFilters && (
        <div
          className="mobile-call-history__filters"
          role="group"
          aria-label={t('mobile.callHistoryFilters', {
            defaultValue: 'Call history filters',
          })}
        >
          <button
            type="button"
            ref={allFilterRef}
            aria-pressed={filter === 'all'}
            onClick={() => {
              lastRestoredFocusId.current = undefined;
              setFilter('all');
            }}
          >
            {t('mobile.recents', { defaultValue: 'Recents' })}
          </button>
          <button
            type="button"
            ref={missedFilterRef}
            aria-pressed={filter === 'missed'}
            onClick={() => {
              lastRestoredFocusId.current = undefined;
              setFilter('missed');
            }}
          >
            {t('mobile.missed', { defaultValue: 'Missed' })}
          </button>
        </div>
      )}

      {filteredItems.length === 0 && (
        <p className="mobile-call-history__filter-empty" role="status">
          {t(filter === 'missed' ? 'mobile.noMissedCalls' : 'mobile.noRecentCalls', {
            defaultValue: filter === 'missed' ? 'No missed calls' : 'No recent calls',
          })}
        </p>
      )}

      <ul className="mobile-call-history__list">
        {filteredItems.map((item) => {
          const isMissed =
            item.disposition?.toUpperCase() === DispositionTypes.MISSED;
          const displayName =
            item.name ||
            item.phoneNumber ||
            item.callbackAddress ||
            labelUnknown ||
            'Unknown';
          const callAddress = item.callbackAddress || item.phoneNumber;
          return (
            <li key={item.id}>
              <div className="mobile-call-history__row">
                <button
                  type="button"
                  ref={(element) => {
                    if (element) {
                      rowRefs.current.set(item.id, element);
                    } else {
                      rowRefs.current.delete(item.id);
                    }
                  }}
                  className="mobile-call-history__row-main"
                  aria-label={`${isMissed ? `${t('missedCallText')}. ` : ''}${t(
                      'mobile.openCallDetails',
                      {
                        defaultValue: `Open call details for ${displayName}`,
                        name: displayName,
                      }
                    )}${item.presenceLabel ? `. ${item.presenceLabel}` : ''}`}
                  onClick={() => {
                    lastRestoredFocusId.current = undefined;
                    restoreFocusId.current = item.id;
                    setSelectedId(item.id);
                    onItemPress(item);
                  }}
                >
                  <CallHistoryAvatar
                    remotePartyId={item.remotePartyId}
                    fallbackId={item.id}
                    displayName={displayName}
                    title={item.name || displayName}
                    size={48}
                    requestSize={40}
                    presenceType={item.presenceType}
                    presenceLabel={item.presenceLabel}
                  />
                  <span className="mobile-call-history__row-content">
                    <strong
                      className={[
                        'mobile-call-history__row-name',
                        isMissed ? 'mobile-call-history__missed' : undefined,
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {displayName}
                    </strong>
                    <span className="mobile-call-history__row-metadata">
                      {[item.phoneNumber || item.callbackAddress, isMissed ? t('missedCallText') : undefined]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                  <span className="mobile-call-history__row-time">
                    <span className="mobile-call-history__row-date">
                      {isLocaleGerman
                        ? formatDateDDMMYYYY(item.startTime)
                        : formatDate(item.startTime)}
                    </span>
                    <span className="mobile-call-history__row-clock">
                      {isLocaleGerman
                        ? formatTimeToSupport24Hours(item.startTime)
                        : formatTime(item.startTime)}
                    </span>
                  </span>
                </button>
                {!isMissed && (
                  <button
                    type="button"
                    className="mobile-call-history__row-call"
                    data-testid="make-audio-call"
                    aria-label={t('audioCallLabel')}
                    disabled={!callAddress}
                    onClick={() => {
                      if (!callAddress) {
                        return;
                      }
                      makeCall(
                        callAddress,
                        false,
                        MAKE_A_CALL_LABELS.RECENT_CALLS
                      );
                    }}
                  >
                    <DesktopOutgoingCallIcon size={15} />
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
};
